import { z } from 'zod';

/**
 * Bedarfsliste / Forecast (Modul forecast, ADR 0035): geplanter Artikelbedarf als
 * Liste — je Zeile ein Artikel (Soft-Ref) mit Menge, voraussichtlichem Liefertermin
 * und Projekt-Referenz (Freitext). Kumulierbar nach Artikel (siehe cumulateForecastLines).
 */
export const createForecastSchema = z.object({
  name: z.string().trim().min(1).max(200),
  notes: z.string().trim().max(5000).optional(),
  /**
   * Lieferant, dem die Vorschau gehört (CRM-Kunde vom Typ SUPPLIER). Eine
   * Bedarfsvorschau wird je Lieferant geführt: Der Wert filtert die Ableitung
   * aus dem Projektbedarf (ADR 0086) und die Artikel-Auswahl der Liste.
   * Weglassen = allgemeine Liste ohne Lieferantenbezug.
   */
  supplierId: z.string().uuid().nullable().optional(),
});
export type CreateForecastInput = z.infer<typeof createForecastSchema>;

export const updateForecastSchema = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  notes: z.string().trim().max(5000).nullable().optional(),
  /**
   * Gibt die Liste für das Partnerportal frei (ADR 0085). **Default false**,
   * auch für Bestandslisten — das ist die einzige Grenze zwischen interner
   * Planung und Lieferantensicht. Freigegeben heißt: Jeder Lieferant sieht die
   * Zeilen der Artikel, für die er als Standardlieferant hinterlegt ist —
   * nicht die ganze Liste, und nie die Projekt-Referenz oder Notizen.
   */
  isPartnerVisible: z.boolean().optional(),
  /** Lieferant der Vorschau; `null` löst die Zuordnung wieder. */
  supplierId: z.string().uuid().nullable().optional(),
});
export type UpdateForecastInput = z.infer<typeof updateForecastSchema>;

/** Eine Bedarfszeile beim Speichern der Liste. */
export const forecastLineInputSchema = z.object({
  articleId: z.string().uuid().nullable().optional(),
  articleNumber: z.string().trim().max(64).nullable().optional(),
  description: z.string().trim().min(1).max(500),
  unit: z.string().trim().max(20).nullable().optional(),
  quantity: z.number().nonnegative(),
  /** Voraussichtlicher Liefertermin (ISO), optional. */
  expectedDate: z.string().datetime().nullable().optional(),
  /** Projekt-Referenz als Freitext, optional. */
  projectText: z.string().trim().max(200).nullable().optional(),
  note: z.string().trim().max(2000).nullable().optional(),
  position: z.number().int().nonnegative().optional(),
  /**
   * Herkunft: aus welchem Projekt wurde die Zeile abgeleitet (ADR 0086)?
   * Gehört ins Eingabe-Schema, damit ein Speichern der ganzen Liste die
   * Herkunft NICHT verliert — sonst würde die nächste Ableitung abgeleitete
   * Zeilen für „von Hand gepflegt" halten und doppelt anlegen.
   */
  sourceProjectId: z.string().uuid().nullable().optional(),
});
export type ForecastLineInput = z.infer<typeof forecastLineInputSchema>;

/** Ganze Liste in einem Rutsch speichern (ersetzt alle Zeilen). */
export const saveForecastLinesSchema = z.object({
  lines: z.array(forecastLineInputSchema).max(2000),
});
export type SaveForecastLinesInput = z.infer<typeof saveForecastLinesSchema>;

export const forecastLineResponseSchema = z.object({
  id: z.string().uuid(),
  forecastId: z.string().uuid(),
  position: z.number().int(),
  articleId: z.string().uuid().nullable(),
  articleNumber: z.string().nullable(),
  description: z.string(),
  unit: z.string().nullable(),
  quantity: z.number(),
  expectedDate: z.string().datetime().nullable(),
  projectText: z.string().nullable(),
  note: z.string().nullable(),
  /** Gesetzt ⇒ aus dem Materialbedarf dieses Projekts abgeleitet (ADR 0086). */
  sourceProjectId: z.string().uuid().nullable(),
});
export type ForecastLineResponse = z.infer<typeof forecastLineResponseSchema>;

/** Zeile inkl. Listen-Name — Basis der Analyse über alle Bedarfslisten. */
export const forecastAnalyticsLineSchema = forecastLineResponseSchema.extend({
  forecastName: z.string(),
});
export type ForecastAnalyticsLine = z.infer<typeof forecastAnalyticsLineSchema>;

/** Forecast-Bedarf je Artikel (Summe + frühester Termin) — für die Bestandsübersicht. */
export const forecastDemandSchema = z.object({
  articleId: z.string().uuid(),
  quantity: z.number(),
  nextExpectedDate: z.string().datetime().nullable(),
});
export type ForecastDemand = z.infer<typeof forecastDemandSchema>;

export const forecastResponseSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  name: z.string(),
  notes: z.string().nullable(),
  /** Für Lieferanten im Partnerportal freigegeben (ADR 0085)? */
  isPartnerVisible: z.boolean(),
  /** Lieferant der Vorschau (null = allgemeine Liste). */
  supplierId: z.string().uuid().nullable(),
  supplierName: z.string().nullable(),
  lines: z.array(forecastLineResponseSchema),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type ForecastResponse = z.infer<typeof forecastResponseSchema>;

/** Listen-Zeile (ohne Positionen) für die Übersicht. */
export const forecastListItemSchema = forecastResponseSchema.omit({ lines: true });
export type ForecastListItem = z.infer<typeof forecastListItemSchema>;

/** Kumulierte Zeile: ein Artikel mit summierter Menge über alle Bedarfe. */
export interface CumulatedForecastLine {
  articleId: string | null;
  articleNumber: string | null;
  description: string;
  unit: string | null;
  totalQuantity: number;
  /** Frühester (nächster) voraussichtlicher Liefertermin der Gruppe, ISO oder null. */
  nextExpectedDate: string | null;
  /** Anzahl zusammengefasster Einzelbedarfe. */
  count: number;
}

/**
 * Fasst Bedarfszeilen nach Artikel zusammen (Menge summiert, frühester Liefertermin,
 * Anzahl). Gruppenschlüssel = articleId, sonst (Freitextzeile) die Beschreibung.
 * Reine Funktion — FE-Anzeige + testbar.
 */
export function cumulateForecastLines(
  lines: Pick<
    ForecastLineResponse,
    'articleId' | 'articleNumber' | 'description' | 'unit' | 'quantity' | 'expectedDate'
  >[],
): CumulatedForecastLine[] {
  const groups = new Map<string, CumulatedForecastLine>();
  for (const line of lines) {
    const key = line.articleId ?? `text:${line.description.trim().toLowerCase()}`;
    const existing = groups.get(key);
    if (existing) {
      existing.totalQuantity += line.quantity;
      existing.count += 1;
      if (line.expectedDate && (!existing.nextExpectedDate || line.expectedDate < existing.nextExpectedDate)) {
        existing.nextExpectedDate = line.expectedDate;
      }
    } else {
      groups.set(key, {
        articleId: line.articleId,
        articleNumber: line.articleNumber,
        description: line.description,
        unit: line.unit,
        totalQuantity: line.quantity,
        nextExpectedDate: line.expectedDate ?? null,
        count: 1,
      });
    }
  }
  return [...groups.values()].sort((a, b) => b.totalQuantity - a.totalQuantity);
}
